import classNames from "classnames";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Direction, getDaysInRangeIncludingEndDate, MIN_THUMB_WIDTH, Range } from "./helpers";
import css from "./scroll_bar.module.scss";
import ThumbEdge from "./thumb_edge";
import useChangeRange from "./use_change_range";
import useScroll from "./use_scroll";

interface ScrollBarProps {
  calendarStartDate: string;
  calendarEndDate: string;
  calendarRef: { current: HTMLElement };
  range: Range;
  daysCount: number;
  onRangeChange: (Range) => void;
  scrollToLeftOffset: (value: number) => void;
}

const ScrollBar = ({
  calendarStartDate,
  calendarEndDate,
  calendarRef,
  range,
  onRangeChange,
  scrollToLeftOffset
}: ScrollBarProps) => {
  const { t } = useTranslation();
  const thumbWidth = useRef<number>(MIN_THUMB_WIDTH);
  const thumbLeft = useRef<number>(0);

  const calendarElement = calendarRef.current;

  const { isDragging, handleThumbMouseDown } = useScroll(
    thumbWidth,
    thumbLeft,
    calendarElement,
    scrollToLeftOffset
  );

  const {
    isDragging: isEdgeDragging,
    handleThumbEdgeMouseDown,
    startDate,
    endDate
  } = useChangeRange(
    thumbWidth,
    thumbLeft,
    calendarElement,
    range,
    onRangeChange,
    calendarStartDate,
    calendarEndDate
  );

  const { utc } = useDateFormatter();

  const rangeInDays = useMemo(
    () => getDaysInRangeIncludingEndDate(utc(startDate), utc(endDate)),
    [startDate, endDate]
  );

  return (
    <div className={css.container}>
      <div className={css.backgroundDots} />
      <div
        className={classNames(css.thumb, { [css.dragging]: isDragging })}
        style={{ width: thumbWidth.current, left: thumbLeft.current }}
      >
        <ThumbEdge
          kind="left"
          onMouseDown={(e) => handleThumbEdgeMouseDown(e, Direction.Left)}
          isDragged={isEdgeDragging}
          value={startDate}
        />
        {/* eslint-disable jsx-a11y/no-static-element-interactions */}
        <div className={css.content} onMouseDown={handleThumbMouseDown}>
          <div className={css.border} />
          {isEdgeDragging && <div className={css.text}>{t("day", { count: rangeInDays })}</div>}
          <div className={css.border} />
        </div>
        <ThumbEdge
          kind="right"
          onMouseDown={(e) => handleThumbEdgeMouseDown(e, Direction.Right)}
          isDragged={isEdgeDragging}
          value={endDate}
        />
      </div>
    </div>
  );
};

export default ScrollBar;
